import { useSyncExternalStore } from 'react';
import MobileDetect from 'mobile-detect';
import { BREAKPOINT_MD_PX } from '@PROJECT_ROOT/assets/responsive-frontend/js/constants/breakpoints';
import windowManipulationService from '@PROJECT_ROOT/assets/responsive-frontend/js/services/windowManipulationService';

const QUERY = `(max-width: ${BREAKPOINT_MD_PX}px)`;

const listeners = new Set();
let mediaQueryList = null;

const getMediaQueryList = () => {
    if (!mediaQueryList) {
        mediaQueryList = window.matchMedia(QUERY);
    }
    return mediaQueryList;
};

// Jedini pravi listener na pregledniku: jedan poziv obavještava sve pretplaćene komponente.
const notifyAll = () => listeners.forEach((listener) => listener());

export const subscribe = (listener) => {
    if (!windowManipulationService.isBrowserEnvironment()) return () => {};

    // Prvi pretplatnik postavlja jedini pravi event listener.
    if (listeners.size === 0) {
        getMediaQueryList().addEventListener('change', notifyAll);
    }
    listeners.add(listener);

    return () => {
        listeners.delete(listener);
        // Zadnji pretplatnik ga uklanja, pa ništa ne ostaje vezano kad nijedna komponenta ne treba podatak.
        if (listeners.size === 0) {
            getMediaQueryList().removeEventListener('change', notifyAll);
        }
    };
};

const getSnapshot = () => getMediaQueryList().matches;

/**
 * Pogađa je li uređaj mobile iz user agenta (na serveru nema viewporta).
 * Koristi se na serveru (proslijedi UA iz requesta) i na klijentu pri hidrataciji,
 * gdje isti UA daje isti rezultat kao na serveru.
 * Napomena: md.mobile() uključuje i tablete, md.phone() samo mobitele.
 */
export const getIsMobileFromUserAgent = (userAgent) => {
    if (!userAgent) return true;
    return new MobileDetect(userAgent).mobile() !== null;
};

// UA se ne mijenja tijekom života stranice, pa se parsira samo jednom.
let cachedClientUserAgentIsMobile = null;
const getClientUserAgentIsMobile = () => {
    if (cachedClientUserAgentIsMobile === null) {
        cachedClientUserAgentIsMobile = getIsMobileFromUserAgent(window.navigator.userAgent);
    }
    return cachedClientUserAgentIsMobile;
};

/**
 * Početna vrijednost za server render i hidrataciju (kad širina nije poznata):
 * 1. SSR vrijednost iz propa, ako postoji,
 * 2. UA procjena u browseru (poklapa se sa serverom jer je isti UA),
 * 3. inače mobile.
 */
export const getInitialIsMobile = (ssrIsMobile) => {
    if (typeof ssrIsMobile === 'boolean') return ssrIsMobile;
    if (windowManipulationService.isBrowserEnvironment()) return getClientUserAgentIsMobile();
    return true;
};

/**
 * Za klasne komponente i ostalo izvan hooka.
 * U browseru uvijek vraća stvarno stanje iz matchMedia, a ssrIsMobile se koristi samo bez windowa.
 */
export const isMobileView = (ssrIsMobile) => {
    if (typeof window === 'undefined') {
        return getInitialIsMobile(ssrIsMobile);
    }

    return getSnapshot();
};

/**
 * @param {boolean} [ssrIsMobile] vrijednost sa servera (npr. getIsMobileFromUserAgent(request UA)).
 *                                Ako nije proslijeđena, pri hidrataciji se koristi UA procjena u browseru.
 */
export default function useIsMobileView(ssrIsMobile) {
    return useSyncExternalStore(
        subscribe,
        getSnapshot,
        () => getInitialIsMobile(ssrIsMobile),
    );
}

export { subscribe as subscribeToMobileView };
